import supabase from '../supabase/supabase';
import { invoiceService } from './invoiceService';

class RecurringService {
  async getSchedules(providedAccountId) {
    const accountId = providedAccountId || await invoiceService.getAccountId();
    if (!accountId) return [];

    try {
      const { data, error } = await supabase
        .schema('accounting')
        .from('recurring_invoices_1786037638223')
        .select(`
          *,
          customer:customer_id ( name, email )
        `)
        .eq('account_id', accountId)
        .order('created_at', { ascending: false });

      if (error) {
        console.warn("Recurring invoices query failed:", error);
        return [];
      }
      return data || [];
    } catch (err) {
      return [];
    }
  }

  async getScheduleById(id) {
    const { data: schedule, error: schError } = await supabase
      .schema('accounting')
      .from('recurring_invoices_1786037638223')
      .select('*')
      .eq('id', id)
      .single();

    if (schError) throw schError;

    const { data: items, error: itemsError } = await supabase
      .schema('accounting')
      .from('recurring_invoice_items_1786037638223')
      .select('*')
      .eq('schedule_id', id)
      .order('created_at');

    if (itemsError) throw itemsError;

    return { ...schedule, items };
  }

  async createSchedule(scheduleData, items, providedAccountId) {
    const accountId = providedAccountId || await invoiceService.getAccountId();
    if (!accountId) throw new Error("Account ID required to create recurring schedule.");

    const { data: schedule, error: schError } = await supabase
      .schema('accounting')
      .from('recurring_invoices_1786037638223')
      .insert([{
        account_id: accountId,
        customer_id: scheduleData.customer_id,
        frequency: scheduleData.frequency,
        start_date: scheduleData.start_date,
        end_date: scheduleData.end_date || null,
        auto_send: scheduleData.auto_send,
        send_reminders: scheduleData.send_reminders,
        amount: scheduleData.amount,
        status: scheduleData.status || 'Active',
        next_date: scheduleData.next_date
      }])
      .select()
      .single();

    if (schError) throw schError;

    if (items && items.length > 0) {
      const itemsToInsert = items.map(item => ({
        schedule_id: schedule.id,
        product: item.product,
        description: item.description,
        qty: item.qty,
        price: item.price,
        tax_rate: item.taxRate || 0,
        discount: item.discount || 0
      }));

      const { error: itemsError } = await supabase
        .schema('accounting')
        .from('recurring_invoice_items_1786037638223')
        .insert(itemsToInsert);

      if (itemsError) throw itemsError;
    }

    return schedule;
  }

  async updateSchedule(id, scheduleData, items) {
    const { data: schedule, error: schError } = await supabase
      .schema('accounting')
      .from('recurring_invoices_1786037638223')
      .update({
        customer_id: scheduleData.customer_id,
        frequency: scheduleData.frequency,
        start_date: scheduleData.start_date,
        end_date: scheduleData.end_date || null,
        auto_send: scheduleData.auto_send,
        send_reminders: scheduleData.send_reminders,
        amount: scheduleData.amount,
        status: scheduleData.status,
        next_date: scheduleData.next_date,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (schError) throw schError;

    // Replace items
    await supabase
      .schema('accounting')
      .from('recurring_invoice_items_1786037638223')
      .delete()
      .eq('schedule_id', id);

    if (items && items.length > 0) {
      const itemsToInsert = items.map(item => ({
        schedule_id: id,
        product: item.product,
        description: item.description,
        qty: item.qty,
        price: item.price,
        tax_rate: item.taxRate || 0,
        discount: item.discount || 0
      }));

      const { error: itemsError } = await supabase
        .schema('accounting')
        .from('recurring_invoice_items_1786037638223')
        .insert(itemsToInsert);

      if (itemsError) throw itemsError;
    }

    return schedule;
  }

  async toggleStatus(id, newStatus) {
    const { error } = await supabase
      .schema('accounting')
      .from('recurring_invoices_1786037638223')
      .update({ status: newStatus, updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) throw error;
    return true;
  }

  async deleteSchedule(id) {
    const { error } = await supabase
      .schema('accounting')
      .from('recurring_invoices_1786037638223')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  }
}

export const recurringService = new RecurringService();